'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { marketingBannerConfig } from '@/constant/marketingBanner';

function getTimeLeft(until: string) {
    const diff = new Date(until).getTime() - Date.now();
    if (diff <= 0) return null;
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { d, h, m, s };
}

export function MarketingBanner() {
    const { enabled, message, discount, suffix, ctaText, ctaHref, activeUntil, countdownLabel } =
        marketingBannerConfig;

    // `timeLeft` stays null for the server render AND the first client render so
    // both produce identical markup (the "00" skeleton below) — that determinism
    // avoids the React #418 hydration text-mismatch error that a Date.now()-based
    // initial value would cause. The live countdown is filled in after mount.
    //
    // Crucially the banner is NOT gated on `timeLeft`: when a campaign is active
    // it renders (with the skeleton) in the SSR/prerendered HTML, so it reserves
    // its height from the first paint and never shifts <main> down (previously a
    // ~0.095 Cumulative Layout Shift). The "00" → live digits swap is a same-width
    // text update, so it produces no layout shift either.
    const [timeLeft, setTimeLeft] = useState<ReturnType<typeof getTimeLeft>>(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!enabled) return;
        setTimeLeft(getTimeLeft(activeUntil));
        const id = setInterval(() => setTimeLeft(getTimeLeft(activeUntil)), 1000);
        return () => clearInterval(id);
    }, [enabled, activeUntil]);

    if (!enabled || dismissed) return null;

    // Two-digit, fixed-width values so the skeleton and the live values occupy the
    // same space. `pad` returns "00" until the post-mount effect provides real ones.
    const pad = (v: number | undefined) => String(v ?? 0).padStart(2, '0');
    const d = pad(timeLeft?.d);
    const h = pad(timeLeft?.h);
    const m = pad(timeLeft?.m);
    const s = pad(timeLeft?.s);

    return (
        <div className="marketing-banner" role="banner">
            <div className="marketing-banner_inner">
                <p className="marketing-banner_text">
                    {message}&nbsp;<strong className="marketing-banner_discount">{discount}</strong>&nbsp;{suffix}
                </p>

                <div className="marketing-banner_countdown" aria-label="Countdown timer">
                    <span className="marketing-banner_countdown-label">{countdownLabel}</span>
                    <span className="marketing-banner_time-unit">
                        <strong>{d}</strong>d
                    </span>
                    <span className="marketing-banner_time-sep">:</span>
                    <span className="marketing-banner_time-unit">
                        <strong>{h}</strong>h
                    </span>
                    <span className="marketing-banner_time-sep">:</span>
                    <span className="marketing-banner_time-unit">
                        <strong>{m}</strong>m
                    </span>
                    <span className="marketing-banner_time-sep">:</span>
                    <span className="marketing-banner_time-unit">
                        <strong>{s}</strong>s
                    </span>
                </div>

                <Link href={ctaHref} className="marketing-banner_cta">
                    {ctaText} →
                </Link>
            </div>

            <button
                type="button"
                className="marketing-banner_close"
                onClick={() => setDismissed(true)}
                aria-label="Dismiss banner"
            >
                ✕
            </button>
        </div>
    );
}
